import React, { useState, useEffect, useCallback } from "react";
import { ScrollText, X } from "lucide-react";
import api from "../../api";

/** Version history dropdown + inline diff for generated docs */
export function VersionHistory({ projectId, filename, currentContent, onSelectVersion }: {
  projectId: string;
  filename: string;
  currentContent: string;
  onSelectVersion: (content: string, version: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState<Array<{ id: string; version: number; content: string; created_at: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [diffView, setDiffView] = useState<{ oldV: number; newV: number; lines: Array<{ type: 'add' | 'del' | 'same'; text: string }> } | null>(null);

  const fetchVersions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getArtifactVersions(projectId, filename);
      setVersions(res.versions);
    } catch { setVersions([]); }
    setLoading(false);
  }, [projectId, filename]);

  useEffect(() => { if (open && versions.length === 0) fetchVersions(); }, [open, fetchVersions, versions.length]);

  // Simple line-based diff
  const computeDiff = (oldText: string, newText: string) => {
    const oldLines = oldText.split('\n');
    const newLines = newText.split('\n');
    const maxLen = Math.max(oldLines.length, newLines.length);
    // Build result with changed lines + context
    const result: Array<{ type: 'add' | 'del' | 'same'; text: string }> = [];
    for (let i = 0; i < maxLen && i < newLines.length; i++) {
      if (oldLines[i] === newLines[i]) {
        result.push({ type: 'same', text: newLines[i] });
      } else {
        if (i < oldLines.length) result.push({ type: 'del', text: oldLines[i] });
        result.push({ type: 'add', text: newLines[i] });
      }
    }
    // Append remaining lines from the longer doc
    for (let i = oldLines.length; i < newLines.length; i++) result.push({ type: 'add', text: newLines[i] });
    for (let i = newLines.length; i < oldLines.length; i++) result.push({ type: 'del', text: oldLines[i] });
    return result;
  };

  if (versions.length <= 1 && !loading && open) {
    return null; // Only one version — no history to show
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          background: 'var(--surface3)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', padding: '4px 10px',
          color: 'var(--text)', cursor: 'pointer', fontSize: 'var(--text-sm)',
        }}
      >
        <ScrollText size={13} /> History
      </button>
      {open && (
        <div
          style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 4,
            background: 'var(--bg-card, #1a1a2e)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)', overflow: 'hidden', zIndex: 100,
            minWidth: 260, maxHeight: 400, overflowY: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          {loading && <div style={{ padding: 12, textAlign: 'center' }}><div className="spinner" style={{ width: 14, height: 14 }} /></div>}
          {!loading && versions.length <= 1 && (
            <div style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
              No previous versions
            </div>
          )}
          {!loading && versions.map((v, i) => {
            const isCurrent = i === 0;
            const date = new Date(v.created_at);
            return (
              <div key={v.id} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 14px', borderBottom: '1px solid var(--border)',
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 'var(--text-sm)', fontWeight: isCurrent ? 600 : 400, color: 'var(--text)' }}>
                    v{v.version}{isCurrent ? ' (current)' : ''}
                  </div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                    {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                {!isCurrent && (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      onClick={() => onSelectVersion(v.content, v.version)}
                      style={{
                        background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                        color: 'var(--blue)', cursor: 'pointer', fontSize: 'var(--text-xs)', padding: '2px 8px',
                      }}
                    >
                      View
                    </button>
                    <button
                      onClick={() => {
                        const diff = computeDiff(v.content, currentContent);
                        setDiffView({ oldV: v.version, newV: versions[0].version, lines: diff });
                      }}
                      style={{
                        background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                        color: 'var(--yellow, #f59e0b)', cursor: 'pointer', fontSize: 'var(--text-xs)', padding: '2px 8px',
                      }}
                    >
                      Diff
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {diffView && (
            <div style={{ borderTop: '2px solid var(--border)', padding: '8px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text)' }}>
                  v{diffView.oldV} → v{diffView.newV}
                </span>
                <button onClick={() => setDiffView(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  <X size={12} />
                </button>
              </div>
              <div style={{
                maxHeight: 200, overflowY: 'auto', fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-xs)', lineHeight: 1.6,
              }}>
                {diffView.lines.filter((l) => l.type !== 'same').slice(0, 100).map((l, i) => (
                  <div key={i} style={{
                    padding: '1px 6px',
                    background: l.type === 'add' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                    color: l.type === 'add' ? 'var(--green, #22c55e)' : 'var(--red, #ef4444)',
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}>
                    {l.type === 'add' ? '+' : '-'} {l.text}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
